import type { LabelRequirements } from '../../api/client';

interface RequirementsViewerProps {
  requirements: LabelRequirements;
  confidence?: number | null;
}

export function RequirementsViewer({ requirements, confidence }: RequirementsViewerProps) {
  return (
    <div className="space-y-4">
      {confidence != null && (
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-gray-700">Confidence:</span>
          <div className="flex-1 max-w-xs bg-gray-200 rounded-full h-2.5">
            <div
              className={`h-2.5 rounded-full ${
                confidence >= 0.8 ? 'bg-green-500' : confidence >= 0.5 ? 'bg-yellow-500' : 'bg-red-500'
              }`}
              style={{ width: `${Math.round(confidence * 100)}%` }}
            />
          </div>
          <span className={`text-sm font-bold ${
            confidence >= 0.8 ? 'text-green-700' : confidence >= 0.5 ? 'text-yellow-700' : 'text-red-700'
          }`}>
            {Math.round(confidence * 100)}%
          </span>
        </div>
      )}

      {requirements.labelTypes.map((lt, idx) => (
        <div key={idx} className="border rounded-lg p-4 bg-white">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-sm font-bold text-gray-900">
              {lt.name || lt.labelType}
            </h4>
            <span className="text-xs text-gray-500">
              {lt.dimensions.width}" × {lt.dimensions.height}" @ {lt.dpi} DPI
            </span>
          </div>

          {/* Required fields */}
          {lt.requiredFields.length > 0 && (
            <div className="mb-3">
              <h5 className="text-xs font-semibold text-gray-500 uppercase mb-1">Required Fields</h5>
              <div className="flex flex-wrap gap-1">
                {lt.requiredFields.map((f, i) => (
                  <span key={i} className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-blue-50 text-blue-700 border border-blue-200">
                    {f.fieldPath}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Optional fields */}
          {lt.optionalFields.length > 0 && (
            <div className="mb-3">
              <h5 className="text-xs font-semibold text-gray-500 uppercase mb-1">Optional Fields</h5>
              <div className="flex flex-wrap gap-1">
                {lt.optionalFields.map((f, i) => (
                  <span key={i} className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-gray-50 text-gray-600 border border-gray-200">
                    {f.fieldPath}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Barcodes */}
          {lt.barcodes.length > 0 && (
            <div>
              <h5 className="text-xs font-semibold text-gray-500 uppercase mb-1">Barcodes</h5>
              <div className="flex flex-wrap gap-1">
                {lt.barcodes.map((b, i) => (
                  <span key={i} className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-purple-50 text-purple-700 border border-purple-200">
                    {b.name} ({b.symbology})
                  </span>
                ))}
              </div>
            </div>
          )}

          {lt.layoutNotes && (
            <p className="mt-2 text-xs text-gray-500 italic">{lt.layoutNotes}</p>
          )}
        </div>
      ))}

      {/* Compliance */}
      {requirements.generalCompliance.specialRequirements.length > 0 && (
        <div className="border rounded-lg p-4 bg-amber-50">
          <h4 className="text-xs font-semibold text-amber-800 uppercase mb-2">Compliance Requirements</h4>
          <ul className="list-disc list-inside text-sm text-amber-900 space-y-1">
            {requirements.generalCompliance.specialRequirements.map((r, i) => (
              <li key={i}>{r}</li>
            ))}
          </ul>
          {requirements.generalCompliance.penalties && (
            <p className="mt-2 text-xs text-red-700">
              ⚠ {requirements.generalCompliance.penalties}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
